import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Marker, Pane } from "react-leaflet";
import L from "leaflet";
import {
  CANDIDATE_MODE_PERCENT,
  CANDIDATE_MODE_VALUES,
  buildCandidateArtifactIndexes,
  buildCandidateMetricRows,
  buildRowsByPrecinct,
  getFeaturePrecinctId,
  getSelectableCandidatesForList,
  isMunicipalCandidateArtifact,
  sortCandidateRows,
} from "./candidates";
import {
  computeAutoBins,
  formatBinRange,
  formatInt,
  formatPct,
  getBinIndex,
  getPaletteSteps,
  getPrecinctLabel,
  MapPane,
  useElectionData,
} from "./Map";
import { BRAND_COLORS, BRAND_MAP_COLOR_PRESETS, normalizeHexColor } from "./branding";
import { getElectionLabel, sortElectionsNewestFirst } from "./electionOrdering";
import {
  getStoredChoiceForElection,
  pickValidChoice,
  pickValidElectionId,
  readElectionSelectionContext,
  setStoredChoiceForElection,
  writeElectionSelectionContext,
} from "./electionSelectionContext";
import { parseAppElectionId, supportsCandidatesModule } from "./electionBehavior";
import {
  clearStoredViewport,
  getInitialMapView,
  makeAutoFitRequestKey,
  readStoredViewport,
  writeStoredViewport,
} from "./mapViewport";
import { getFeatureRepresentativePoint } from "./landmarks";
import { ELECTION_MAP_STANDARD_V1, ELECTION_MAP_STANDARD_V1_KEY, getElectionMapFeatureStyle } from "./mapRenderingStandard";

const EMPTY_GEOJSON = { type: "FeatureCollection", features: [] };
const CANDIDATE_ARTIFACT_MISSING_CODE = "candidate_artifact_missing";
const CANDIDATES_STORAGE_KEY = "electionmap.candidates.v1";
const NO_DATA_FILL_COLOR = "#e2e8f0";
const NEUTRAL_FILL_COLOR = "#ffffff";
const NEUTRAL_SELECTED_FILL_COLOR = "#e0f2fe";
const CANDIDATE_LABEL_PANE = "volby-candidates-labels";
const DEFAULT_SCALE_COLOR = BRAND_MAP_COLOR_PRESETS[1]?.hex || BRAND_COLORS.primaryBrightBlue;
const COLOR_OPTIONS = [
  { name: "Tmavě modrá", hex: BRAND_COLORS.primaryDarkBlue },
  ...BRAND_MAP_COLOR_PRESETS,
].filter((item, index, arr) => arr.findIndex((candidate) => candidate.hex === item.hex) === index);

function createHttpError(status, label, payload) {
  const error = new Error(`${label} HTTP ${status}`);
  error.status = Number(status);
  error.payload = payload || null;
  return error;
}

async function parseJsonSafe(response) {
  try {
    return await response.json();
  } catch (_) {
    return null;
  }
}

function readStoredCandidatesSettings() {
  if (typeof window === "undefined" || !window.localStorage) return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(CANDIDATES_STORAGE_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_) {
    return {};
  }
}

function writeStoredCandidatesSettings(settings) {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(CANDIDATES_STORAGE_KEY, JSON.stringify(settings && typeof settings === "object" ? settings : {}));
  } catch (_) {
    // Local module preferences are best-effort only.
  }
}

function getColorPopoverPosition(anchorElement) {
  if (!anchorElement || typeof anchorElement.getBoundingClientRect !== "function") {
    return { top: 96, left: 24 };
  }
  const rect = anchorElement.getBoundingClientRect();
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1280;
  const popoverWidth = 236;
  const left = Math.max(12, Math.min(rect.right - popoverWidth, viewportWidth - popoverWidth - 12));
  return {
    top: Math.max(12, rect.bottom + 8),
    left,
  };
}

function CandidatesColorPresetPicker({ label, value, openPicker, setOpenPicker, onChange }) {
  const triggerRef = useRef(null);
  const activeColor = normalizeHexColor(value) || DEFAULT_SCALE_COLOR;
  const isOpen = openPicker?.key === "scale";

  function openPalette() {
    setOpenPicker({
      key: "scale",
      ...getColorPopoverPosition(triggerRef.current),
    });
  }

  function closePalette() {
    setOpenPicker((current) => (current?.key === "scale" ? null : current));
  }

  return (
    <div className="candidates-color-picker">
      <button
        ref={triggerRef}
        type="button"
        className="candidates-color-trigger"
        aria-label={`Upravit barvu: ${label}`}
        aria-expanded={isOpen}
        onClick={openPalette}
      >
        <span className="candidates-color-button-swatch" style={{ background: activeColor }} aria-hidden="true" />
      </button>
      {isOpen ? (
        <div
          className="candidates-color-menu"
          style={{ top: openPicker.top, left: openPicker.left }}
          role="dialog"
          aria-label={`Vyberte barvu pro ${label}`}
        >
          <div className="candidates-color-menu-header">
            <div className="candidates-color-menu-title">Vyberte barvu</div>
            <button type="button" className="candidates-color-close" aria-label="Zavřít výběr barvy" onClick={closePalette}>
              ×
            </button>
          </div>
          <div className="candidates-color-grid">
            {COLOR_OPTIONS.map((preset) => {
              const color = normalizeHexColor(preset.hex);
              if (!color) return null;
              const isActive = color === activeColor;
              return (
                <button
                  key={color}
                  type="button"
                  className={`candidates-color-preset${isActive ? " is-active" : ""}`}
                  style={{ background: color }}
                  aria-label={`${preset.name}: ${label}`}
                  aria-pressed={isActive}
                  title={preset.name}
                  onClick={() => onChange(color)}
                />
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function getElectionId(election) {
  return String(election?.id || election?.election_id || "").trim();
}

function compareLists(a, b) {
  const orderA = Number(a?.party_order);
  const orderB = Number(b?.party_order);
  if (Number.isFinite(orderA) && Number.isFinite(orderB) && orderA !== orderB) return orderA - orderB;
  if (Number.isFinite(orderA) && !Number.isFinite(orderB)) return -1;
  if (!Number.isFinite(orderA) && Number.isFinite(orderB)) return 1;
  return String(a?.party_name || "").localeCompare(String(b?.party_name || ""), "cs-CZ", {
    numeric: true,
    sensitivity: "base",
  });
}

function getFeaturePrecinctLabel(feature, electionId) {
  const properties = feature?.properties || {};
  const municipalityName = String(properties.naz_mco || properties.naz_obec || "").trim();
  return getPrecinctLabel(
    {
      raw: getFeaturePrecinctId(feature),
      areaCsuCode: properties.area_csu_code,
      municipalityName,
    },
    electionId,
    null
  );
}

function getCandidateDisplayName(candidate) {
  const order = Number(candidate?.candidate_order);
  const prefix = Number.isFinite(order) ? `${order}. ` : "";
  return `${prefix}${String(candidate?.candidate_name || candidate?.candidate_id || "").trim()}`;
}

function formatSignedInt(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "–";
  return `${n >= 0 ? "+" : ""}${formatInt(n)}`;
}

function formatDifferenceValue(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "–";
  if (n === 0) return "0";
  return `${n > 0 ? "+" : ""}${formatInt(n)}`;
}

function getDifferenceClassName(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "is-zero";
  return n > 0 ? "is-positive" : "is-negative";
}

function formatValueForMode(row, isMunicipal) {
  if (!row || !Number.isFinite(Number(row.absoluteValue))) return "–";
  return isMunicipal ? formatSignedInt(row.absoluteValue) : formatInt(row.absoluteValue);
}

function createCandidateLabelIcon(label, { isMunicipal = false } = {}) {
  const text = String(label || "").replace(/[&<>"']/g, (char) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char]
  ));
  return L.divIcon({
    className: "candidates-precinct-label-icon",
    html: `<span class="candidates-precinct-label${isMunicipal ? " is-bonus" : ""}">${text}</span>`,
    iconSize: [48, 16],
    iconAnchor: [24, 8],
  });
}

function CandidateValueLabelsLayer({ labels, isMunicipal }) {
  if (!Array.isArray(labels) || labels.length === 0) return null;
  return (
    <Pane name={CANDIDATE_LABEL_PANE} style={{ zIndex: ELECTION_MAP_STANDARD_V1.paneZIndexes.labels + 16, pointerEvents: "none" }}>
      {labels.map((label) => (
        <Marker
          key={`${label.precinctId}:${label.valueKey}`}
          position={[label.lat, label.lng]}
          icon={createCandidateLabelIcon(label.text, { isMunicipal })}
          interactive={false}
          keyboard={false}
        />
      ))}
    </Pane>
  );
}

function MapLegend({ legendItems }) {
  if (!legendItems.length) return <div className="candidates-legend-empty">Legenda není k dispozici.</div>;
  return (
    <div className="candidates-legend-list">
      {legendItems.map((item) => (
        <div key={item.label} className="candidates-legend-row">
          <span className="candidates-legend-swatch" style={{ background: item.color }} aria-hidden="true" />
          <span className="candidates-legend-text">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

export default function CandidatesPage({ areaId }) {
  const storedSettings = useMemo(() => readStoredCandidatesSettings(), []);
  const [elections, setElections] = useState([]);
  const [electionsError, setElectionsError] = useState("");
  const [isLoadingElections, setIsLoadingElections] = useState(false);
  const [electionId, setElectionId] = useState("");
  const [candidateArtifact, setCandidateArtifact] = useState(null);
  const [candidateError, setCandidateError] = useState("");
  const [isCandidateArtifactMissing, setIsCandidateArtifactMissing] = useState(false);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [selectedListId, setSelectedListId] = useState("");
  const [selectedCandidateId, setSelectedCandidateId] = useState("");
  const [comparisonCandidateId, setComparisonCandidateId] = useState("");
  const [metricMode, setMetricMode] = useState(CANDIDATE_MODE_VALUES);
  const [sortKey, setSortKey] = useState("precinct");
  const [sortDirection, setSortDirection] = useState("asc");
  const [baseColor, setBaseColor] = useState(() => normalizeHexColor(storedSettings.baseColor) || DEFAULT_SCALE_COLOR);
  const [openPicker, setOpenPicker] = useState(null);
  const [hoveredPrecinct, setHoveredPrecinct] = useState(null);
  const [selectedPrecinctIds, setSelectedPrecinctIds] = useState(() => new Set());
  const [tooltip, setTooltip] = useState(null);
  const mapRef = useRef(null);
  const layerIndexRef = useRef(new Map());
  const prevAreaIdRef = useRef(areaId);
  const initialViewport = useMemo(() => getInitialMapView("candidates", areaId), [areaId]);
  const hasStoredInitialViewportRef = useRef(Boolean(readStoredViewport("candidates", areaId)));
  const [fitRequestKey, setFitRequestKey] = useState(() =>
    hasStoredInitialViewportRef.current ? "" : makeAutoFitRequestKey("candidates", areaId, "initial")
  );

  useEffect(() => {
    writeStoredCandidatesSettings({ baseColor });
  }, [baseColor]);

  useEffect(() => {
    if (!areaId) {
      setElections([]);
      setElectionId("");
      return undefined;
    }

    const ac = new AbortController();
    setIsLoadingElections(true);
    setElectionsError("");
    setElections([]);
    setElectionId("");

    (async () => {
      try {
        const response = await fetch(`/api/areas/${encodeURIComponent(String(areaId))}/elections`, {
          credentials: "include",
          signal: ac.signal,
        });
        if (!response.ok) throw createHttpError(response.status, "enabled elections");
        const payload = await response.json();
        if (ac.signal.aborted) return;
        const nextElections = sortElectionsNewestFirst(Array.isArray(payload) ? payload : []).filter((election) =>
          supportsCandidatesModule(parseAppElectionId(getElectionId(election)).election_key)
        );
        const storedSelection = readElectionSelectionContext(areaId);
        const nextElectionId = pickValidElectionId(
          nextElections,
          [storedSelection.candidatesElectionId, storedSelection.lastElectionId],
          getElectionId(nextElections[0])
        );
        setElections(nextElections);
        setElectionId(nextElectionId);
      } catch (err) {
        if (err?.name === "AbortError") return;
        setElectionsError(Number(err?.status) === 403 ? "Tato oblast není pro váš účet dostupná." : "Nepodařilo se načíst povolené volby.");
      } finally {
        if (!ac.signal.aborted) setIsLoadingElections(false);
      }
    })();

    return () => ac.abort();
  }, [areaId]);

  useEffect(() => {
    if (!areaId || !electionId) {
      setCandidateArtifact(null);
      setCandidateError("");
      setIsCandidateArtifactMissing(false);
      return undefined;
    }

    const ac = new AbortController();
    setCandidateArtifact(null);
    setCandidateError("");
    setIsCandidateArtifactMissing(false);
    setIsLoadingCandidates(true);
    setSelectedListId("");
    setSelectedCandidateId("");
    setComparisonCandidateId("");

    (async () => {
      try {
        const url = `/api/candidates?area_id=${encodeURIComponent(String(areaId))}&election_id=${encodeURIComponent(electionId)}`;
        const response = await fetch(url, { credentials: "include", signal: ac.signal });
        const payload = await parseJsonSafe(response);
        if (!response.ok) throw createHttpError(response.status, "candidates", payload);
        if (ac.signal.aborted) return;
        setCandidateArtifact(payload && typeof payload === "object" ? payload : null);
      } catch (err) {
        if (err?.name === "AbortError") return;
        setCandidateArtifact(null);
        const code = err?.payload && typeof err.payload.code === "string" ? err.payload.code : "";
        if (Number(err?.status) === 404 && code === CANDIDATE_ARTIFACT_MISSING_CODE) {
          setIsCandidateArtifactMissing(true);
          setCandidateError("");
        } else {
          setCandidateError(Number(err?.status) === 403 ? "Kandidáti nejsou pro váš účet dostupní." : "Nepodařilo se načíst kandidátní data.");
        }
      } finally {
        if (!ac.signal.aborted) setIsLoadingCandidates(false);
      }
    })();

    return () => ac.abort();
  }, [areaId, electionId]);

  useEffect(() => {
    if (prevAreaIdRef.current === areaId) return;
    prevAreaIdRef.current = areaId;
    setSelectedPrecinctIds(new Set());
    setHoveredPrecinct(null);
    clearStoredViewport("candidates", areaId);
    setFitRequestKey(makeAutoFitRequestKey("candidates", areaId, "area"));
  }, [areaId]);

  const data = useElectionData(electionId, areaId);
  const features = useMemo(() => (Array.isArray(data.districtsGeoJson?.features) ? data.districtsGeoJson.features : []), [data.districtsGeoJson]);
  const isMunicipal = isMunicipalCandidateArtifact(candidateArtifact);
  const lists = useMemo(
    () => [...(Array.isArray(candidateArtifact?.lists) ? candidateArtifact.lists : [])].sort(compareLists),
    [candidateArtifact]
  );
  const selectedList = useMemo(
    () => lists.find((list) => String(list?.list_id) === String(selectedListId)) || null,
    [lists, selectedListId]
  );
  const selectableCandidates = useMemo(
    () => getSelectableCandidatesForList(candidateArtifact, selectedList),
    [candidateArtifact, selectedList]
  );
  const candidateIndexes = useMemo(() => buildCandidateArtifactIndexes(candidateArtifact), [candidateArtifact]);
  const selectedCandidate = selectedCandidateId ? candidateIndexes.candidateById.get(selectedCandidateId) || null : null;
  const paletteSteps = useMemo(() => getPaletteSteps(baseColor), [baseColor]);

  useEffect(() => {
    if (!candidateArtifact || lists.length === 0) {
      if (selectedListId) setSelectedListId("");
      return;
    }
    const options = lists.map((list) => String(list?.list_id || "")).filter(Boolean);
    if (selectedListId && options.includes(selectedListId)) return;
    const storedSelection = readElectionSelectionContext(areaId);
    const nextListId = pickValidChoice(
      options,
      getStoredChoiceForElection(storedSelection, "candidatesListByElection", electionId),
      options[0] || ""
    );
    if (nextListId !== selectedListId) setSelectedListId(nextListId);
  }, [areaId, candidateArtifact, electionId, lists, selectedListId]);

  useEffect(() => {
    const options = selectableCandidates.map((candidate) => String(candidate?.candidate_id || "")).filter(Boolean);
    if (options.length === 0) {
      if (selectedCandidateId) setSelectedCandidateId("");
      return;
    }
    if (selectedCandidateId && options.includes(selectedCandidateId)) return;
    const storedSelection = readElectionSelectionContext(areaId);
    const nextCandidateId = pickValidChoice(
      options,
      getStoredChoiceForElection(storedSelection, "candidatesCandidateByElection", electionId),
      options[0] || ""
    );
    if (nextCandidateId !== selectedCandidateId) setSelectedCandidateId(nextCandidateId);
  }, [areaId, electionId, selectableCandidates, selectedCandidateId]);

  useEffect(() => {
    const options = selectableCandidates.map((candidate) => String(candidate?.candidate_id || "")).filter(Boolean);
    if (!comparisonCandidateId) return;
    if (options.includes(comparisonCandidateId) && comparisonCandidateId !== selectedCandidateId) return;
    setComparisonCandidateId("");
  }, [comparisonCandidateId, selectableCandidates, selectedCandidateId]);

  useEffect(() => {
    if (!comparisonCandidateId && sortKey === "difference") setSortKey("precinct");
  }, [comparisonCandidateId, sortKey]);

  useEffect(() => {
    if (!areaId || !electionId) return;
    writeElectionSelectionContext(areaId, {
      candidatesElectionId: electionId,
      candidatesListByElection: setStoredChoiceForElection(
        readElectionSelectionContext(areaId),
        "candidatesListByElection",
        electionId,
        selectedListId
      ),
      candidatesCandidateByElection: setStoredChoiceForElection(
        readElectionSelectionContext(areaId),
        "candidatesCandidateByElection",
        electionId,
        selectedCandidateId
      ),
      lastElectionId: electionId,
    });
  }, [areaId, electionId, selectedCandidateId, selectedListId]);

  useEffect(() => {
    layerIndexRef.current = new Map();
    setHoveredPrecinct(null);
    setSelectedPrecinctIds(new Set());
    setTooltip(null);
    if (electionId) setFitRequestKey(makeAutoFitRequestKey("candidates", areaId, `election-${electionId}`));
  }, [areaId, electionId]);

  const rows = useMemo(
    () =>
      buildCandidateMetricRows({
        artifact: candidateArtifact,
        selectedListId,
        selectedCandidateId,
        features,
        results: data.results,
        stats: data.stats,
      }),
    [candidateArtifact, data.results, data.stats, features, selectedCandidateId, selectedListId]
  );
  const rowsByPrecinct = useMemo(() => buildRowsByPrecinct(rows), [rows]);
  const comparisonRows = useMemo(
    () =>
      comparisonCandidateId
        ? buildCandidateMetricRows({
            artifact: candidateArtifact,
            selectedListId,
            selectedCandidateId: comparisonCandidateId,
            features,
            results: data.results,
            stats: data.stats,
          })
        : [],
    [candidateArtifact, comparisonCandidateId, data.results, data.stats, features, selectedListId]
  );
  const comparisonRowsByPrecinct = useMemo(() => buildRowsByPrecinct(comparisonRows), [comparisonRows]);
  const comparisonCandidate = comparisonCandidateId ? candidateIndexes.candidateById.get(comparisonCandidateId) || null : null;
  const comparisonCandidateOptions = useMemo(
    () => selectableCandidates.filter((candidate) => String(candidate?.candidate_id || "") !== String(selectedCandidateId || "")),
    [selectableCandidates, selectedCandidateId]
  );
  const displayRows = useMemo(
    () =>
      rows.map((row) => {
        const comparisonRow = comparisonRowsByPrecinct.get(String(row.precinctId));
        const comparisonValue = comparisonRow && Number.isFinite(Number(comparisonRow.absoluteValue)) ? Number(comparisonRow.absoluteValue) : null;
        const ownValue = Number.isFinite(Number(row.absoluteValue)) ? Number(row.absoluteValue) : null;
        return {
          ...row,
          comparisonRow,
          comparisonValue,
          differenceValue: ownValue !== null && comparisonValue !== null ? ownValue - comparisonValue : null,
        };
      }),
    [comparisonRowsByPrecinct, rows]
  );
  const sortedRows = useMemo(
    () => sortCandidateRows(displayRows, sortKey, candidateArtifact?.model, sortDirection),
    [candidateArtifact, displayRows, sortDirection, sortKey]
  );
  const valuesPct = useMemo(
    () => rows.map((row) => Number(row.percentValue)).filter((value) => Number.isFinite(value)),
    [rows]
  );
  const bins = useMemo(() => computeAutoBins(valuesPct, 4), [valuesPct]);
  const fillColorForPct = useCallback(
    (pct) => {
      const idx = getBinIndex(pct, bins);
      if (idx < 0) return NO_DATA_FILL_COLOR;
      return paletteSteps[Math.min(idx, paletteSteps.length - 1)] || paletteSteps[paletteSteps.length - 1];
    },
    [bins, paletteSteps]
  );
  const legendItems = useMemo(() => {
    if (metricMode !== CANDIDATE_MODE_PERCENT) return [];
    const items = [];
    for (let index = 0; index < bins.length - 1; index += 1) {
      items.push({
        label: formatBinRange(bins[index], bins[index + 1]),
        color: paletteSteps[Math.min(index, paletteSteps.length - 1)] || DEFAULT_SCALE_COLOR,
      });
    }
    items.push({ label: "bez dat", color: NO_DATA_FILL_COLOR });
    return items;
  }, [bins, metricMode, paletteSteps]);

  const styleFeature = useCallback(
    (feature) => {
      const precinctId = getFeaturePrecinctId(feature);
      const row = rowsByPrecinct.get(precinctId);
      const isHovered = hoveredPrecinct != null && String(hoveredPrecinct) === precinctId;
      const isSelected = selectedPrecinctIds.has(precinctId);
      if (metricMode === CANDIDATE_MODE_VALUES) {
        const style = getElectionMapFeatureStyle({
          fillColor: isSelected ? NEUTRAL_SELECTED_FILL_COLOR : NEUTRAL_FILL_COLOR,
          noDataFillColor: NEUTRAL_FILL_COLOR,
          isHovered,
          isSelected,
          clearOnFocus: false,
        });
        return {
          ...style,
          color: isSelected ? "#2563eb" : isHovered ? "#334155" : "#64748b",
          weight: isSelected ? 3.2 : isHovered ? 2.4 : 1.15,
          opacity: isSelected || isHovered ? 0.98 : 0.74,
          fillOpacity: isSelected ? 0.18 : isHovered ? 0.06 : 0.025,
        };
      }
      return getElectionMapFeatureStyle({
        fillColor: fillColorForPct(row?.percentValue),
        noDataFillColor: NO_DATA_FILL_COLOR,
        isHovered,
        isSelected,
        clearOnFocus: true,
      });
    },
    [fillColorForPct, hoveredPrecinct, metricMode, rowsByPrecinct, selectedPrecinctIds]
  );

  useEffect(() => {
    for (const layer of layerIndexRef.current.values()) {
      try {
        layer.setStyle(styleFeature(layer.feature));
      } catch (_) {}
    }
  }, [styleFeature]);

  const showTooltipAtEvent = useCallback((precinctId, event) => {
    const originalEvent = event?.originalEvent || event;
    const clientX = Number(originalEvent?.clientX);
    const clientY = Number(originalEvent?.clientY);
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return;
    const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1440;
    const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 900;
    setTooltip({
      precinctId: String(precinctId || ""),
      x: Math.max(12, Math.min(clientX + 18, viewportWidth - 330)),
      y: Math.max(12, Math.min(clientY + 18, viewportHeight - 220)),
    });
  }, []);

  const toggleSelectedPrecinct = useCallback((precinctId) => {
    const id = String(precinctId || "");
    if (!id) return;
    setSelectedPrecinctIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const onEachFeature = useCallback(
    (feature, layer) => {
      const precinctId = getFeaturePrecinctId(feature);
      if (!precinctId) return;
      layerIndexRef.current.set(precinctId, layer);
      layer.on({
        mouseover: (event) => {
          setHoveredPrecinct(precinctId);
          showTooltipAtEvent(precinctId, event);
        },
        mousemove: (event) => showTooltipAtEvent(precinctId, event),
        mouseout: () => {
          setHoveredPrecinct((current) => (String(current || "") === precinctId ? null : current));
          setTooltip(null);
        },
        click: () => toggleSelectedPrecinct(precinctId),
      });
    },
    [showTooltipAtEvent, toggleSelectedPrecinct]
  );

  const valueLabels = useMemo(() => {
    if (metricMode !== CANDIDATE_MODE_VALUES) return [];
    return features
      .map((feature) => {
        const precinctId = getFeaturePrecinctId(feature);
        const row = rowsByPrecinct.get(precinctId);
        const point = getFeatureRepresentativePoint(feature);
        if (!precinctId || !row || !point || !Number.isFinite(Number(row.absoluteValue))) return null;
        return {
          precinctId,
          text: formatValueForMode(row, isMunicipal),
          valueKey: `${row.candidateId}:${row.absoluteValue}:${row.percentValue ?? ""}`,
          lat: point.lat,
          lng: point.lng,
        };
      })
      .filter(Boolean);
  }, [features, isMunicipal, metricMode, rowsByPrecinct]);

  const tooltipRow = useMemo(() => {
    if (!tooltip?.precinctId) return null;
    const feature = features.find((item) => getFeaturePrecinctId(item) === tooltip.precinctId);
    const row = rowsByPrecinct.get(tooltip.precinctId);
    if (!row) return null;
    return {
      ...row,
      precinctLabel: feature ? getFeaturePrecinctLabel(feature, electionId) : `Okrsek ${tooltip.precinctId}`,
    };
  }, [electionId, features, rowsByPrecinct, tooltip]);

  const resetMapToArea = useCallback(() => {
    setSelectedPrecinctIds(new Set());
    setHoveredPrecinct(null);
    setTooltip(null);
    clearStoredViewport("candidates", areaId);
    setFitRequestKey(makeAutoFitRequestKey("candidates", areaId, `reset-${electionId || "none"}`));
  }, [areaId, electionId]);

  const loadingText = [
    isLoadingElections ? "volby" : "",
    data.isLoadingDistricts ? "okrsky" : "",
    data.isLoadingResults ? "výsledky" : "",
    data.isLoadingStats ? "statistiky" : "",
    isLoadingCandidates ? "kandidáti" : "",
  ].filter(Boolean).join(" + ");
  const featureCount = features.length;
  const geometryBlockingError =
    data.districtsError || (!data.isLoadingDistricts && electionId && featureCount === 0 ? "Geometrie okrsků není k dispozici." : "");
  const softWarnings = [electionsError, data.resultsError, data.statsError, candidateError].filter(Boolean);
  const electionLabel = getElectionLabel(elections.find((item) => String(item.id) === String(electionId)) || { id: electionId });
  const dataTitle = `Data - ${formatInt(sortedRows.length)} okrsků - ${selectedCandidate?.candidate_name || electionLabel || "Kandidáti"}`;
  const mapKey = `candidates-${areaId || "none"}`;
  const geoJsonKey = `${electionId || "none"}-${featureCount}`;
  const canShowCandidateControls = Boolean(candidateArtifact && !isCandidateArtifactMissing);
  const emptyState = isCandidateArtifactMissing ? "Kandidáti pro tuto volbu zatím nejsou k dispozici." : "";

  return (
    <main className="candidates-page">
      <style>{`
        .candidates-page {
          position: relative;
          z-index: 0;
          width: 100%;
          min-height: calc(var(--app-shell-available-height, 100vh) - 20px);
          padding: 12px 0 24px;
          box-sizing: border-box;
          color: #0f172a;
          --candidates-map-height: clamp(360px, calc(var(--app-shell-available-height, 100vh) - 170px), 500px);
        }
        .candidates-shell {
          width: min(1320px, calc(100% - 36px));
          margin: 0 auto;
          display: grid;
          grid-template-columns: minmax(286px, 330px) minmax(0, 1fr);
          align-items: start;
          gap: 12px;
        }
        .candidates-panel,
        .candidates-map-frame,
        .candidates-data-section {
          border: 1px solid #d7e1eb;
          border-radius: 8px;
          background: #ffffff;
          box-shadow: 0 12px 30px rgba(15, 23, 42, 0.07);
        }
        .candidates-panel {
          padding: 14px;
          display: grid;
          gap: 14px;
          align-content: start;
          max-height: var(--candidates-map-height);
          overflow: auto;
        }
        .candidates-control,
        .candidates-legend {
          display: grid;
          gap: 6px;
          min-width: 0;
        }
        .candidates-control > span,
        .candidates-section-label {
          font-size: 0.74rem;
          font-weight: 800;
          text-transform: uppercase;
          color: #475569;
        }
        .candidates-control select {
          width: 100%;
          min-height: 34px;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          background: #ffffff;
          color: #0f172a;
          font-size: 0.86rem;
          font-weight: 700;
          padding: 7px 10px;
        }
        .candidates-mode-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
        }
        .candidates-mode-button,
        .candidates-report-button,
        .candidates-data-sort-button {
          min-height: 30px;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          background: #ffffff;
          color: #1e293b;
          cursor: pointer;
          font-size: 0.8rem;
          font-weight: 800;
          padding: 5px 9px;
        }
        .candidates-mode-button.is-active {
          border-color: ${BRAND_COLORS.primaryBrightBlue};
          background: #eff6ff;
          color: ${BRAND_COLORS.primaryDarkBlue};
        }
        .candidates-report-button:disabled {
          opacity: 0.52;
          cursor: default;
        }
        .candidates-panel-actions {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          flex-wrap: wrap;
          gap: 8px;
        }
        .candidates-map-frame {
          position: relative;
          z-index: 0;
          min-width: 0;
          height: var(--candidates-map-height);
          overflow: hidden;
          background: #f8fafc;
        }
        .candidates-map-frame .leaflet-container {
          z-index: 0;
        }
        .candidates-map-frame > div {
          height: 100%;
          border: 0 !important;
          border-radius: 0 !important;
        }
        .candidates-map-error,
        .candidates-map-empty {
          position: absolute;
          left: 50%;
          top: 50%;
          z-index: 720;
          transform: translate(-50%, -50%);
          width: min(460px, calc(100vw - 36px));
          padding: 16px;
          text-align: center;
          font-weight: 900;
          background: rgba(255, 255, 255, 0.94);
          border-radius: 8px;
        }
        .candidates-map-error {
          color: #991b1b;
          border: 1px solid #fecaca;
        }
        .candidates-map-empty {
          color: #1e3a8a;
          border: 1px solid #bfdbfe;
        }
        .candidates-status {
          display: grid;
          gap: 6px;
        }
        .candidates-status span {
          border-radius: 999px;
          padding: 6px 9px;
          background: #eff6ff;
          color: #1e3a8a;
          font-size: 0.78rem;
          font-weight: 800;
        }
        .candidates-status .is-error {
          color: #991b1b;
          background: #fef2f2;
        }
        .candidates-legend-control-row,
        .candidates-legend-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          min-height: 34px;
        }
        .candidates-legend-list {
          display: grid;
          gap: 7px;
        }
        .candidates-legend-swatch {
          width: 32px;
          height: 14px;
          border-radius: 999px;
          border: 1px solid rgba(15, 23, 42, 0.14);
        }
        .candidates-legend-text,
        .candidates-legend-empty {
          flex: 1 1 auto;
          font-size: 0.8rem;
          font-weight: 800;
          color: #334155;
        }
        .candidates-legend-empty {
          color: #64748b;
        }
        .candidates-color-picker {
          position: relative;
          display: inline-flex;
          align-items: center;
        }
        .candidates-color-trigger,
        .candidates-color-close {
          width: 34px;
          height: 30px;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          background: #ffffff;
          cursor: pointer;
          display: grid;
          place-items: center;
          color: #1e293b;
          font-weight: 900;
        }
        .candidates-color-button-swatch {
          width: 18px;
          height: 18px;
          border-radius: 5px;
          border: 1px solid rgba(15, 23, 42, 0.18);
        }
        .candidates-color-menu {
          position: fixed;
          z-index: 2200;
          width: 236px;
          border: 1px solid #d7e1eb;
          border-radius: 8px;
          background: #ffffff;
          box-shadow: 0 18px 38px rgba(15, 23, 42, 0.18);
          padding: 12px;
          display: grid;
          gap: 12px;
        }
        .candidates-color-menu-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }
        .candidates-color-menu-title {
          font-size: 0.74rem;
          font-weight: 800;
          color: #334155;
        }
        .candidates-color-close {
          width: 28px;
          height: 28px;
          font-size: 1rem;
        }
        .candidates-color-grid {
          display: grid;
          grid-template-columns: repeat(6, 26px);
          gap: 8px;
        }
        .candidates-color-preset {
          width: 26px;
          height: 26px;
          border: 1px solid rgba(15, 23, 42, 0.12);
          border-radius: 6px;
          cursor: pointer;
        }
        .candidates-color-preset.is-active {
          outline: 2px solid ${BRAND_COLORS.primaryDarkBlue};
          outline-offset: 2px;
        }
        .candidates-precinct-label-icon {
          background: transparent;
          border: 0;
          pointer-events: none;
        }
        .candidates-precinct-label {
          display: inline-grid;
          place-items: center;
          min-width: 20px;
          max-width: 58px;
          height: 17px;
          padding: 0 3px;
          border-radius: 3px;
          background: rgba(255, 255, 255, 0.44);
          color: #1d4ed8;
          text-shadow: 0 1px 1px rgba(255, 255, 255, 0.92);
          font-size: 11px;
          font-weight: 900;
          line-height: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          pointer-events: none;
        }
        .candidates-precinct-label.is-bonus {
          background: rgba(239, 246, 255, 0.48);
          color: #0284c7;
          font-weight: 950;
        }
        .candidates-data-section {
          width: min(1480px, calc(100% - 20px));
          margin: 12px auto 0;
          padding: 14px;
          box-sizing: border-box;
        }
        .candidates-data-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 12px;
        }
        .candidates-data-title {
          display: inline-flex;
          align-items: baseline;
          gap: 12px;
          min-width: 0;
        }
        .candidates-data-header h2 {
          margin: 0;
          color: #0f172a;
          font-size: 1.08rem;
          font-weight: 900;
          line-height: 1.2;
        }
        .candidates-data-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .candidates-data-control {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          min-width: 0;
        }
        .candidates-data-actions select {
          min-height: 32px;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          background: #ffffff;
          color: #0f172a;
          font-size: 0.82rem;
          font-weight: 800;
          padding: 6px 10px;
          max-width: min(360px, 48vw);
        }
        .candidates-sort-direction-button {
          width: 32px;
          min-height: 32px;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          background: #ffffff;
          color: #0f172a;
          cursor: pointer;
          font-size: 1rem;
          font-weight: 900;
          line-height: 1;
        }
        .candidates-sort-direction-button:hover {
          border-color: ${BRAND_COLORS.primaryBrightBlue};
          color: ${BRAND_COLORS.primaryDarkBlue};
        }
        .candidates-data-empty {
          margin: 0;
          color: #64748b;
          font-size: 0.88rem;
          font-weight: 700;
        }
        .candidates-data-table-wrap {
          max-height: min(520px, 62vh);
          overflow: auto;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
        }
        .candidates-data-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.88rem;
        }
        .candidates-data-table th,
        .candidates-data-table td {
          padding: 9px 10px;
          border-bottom: 1px solid #e2e8f0;
          text-align: left;
        }
        .candidates-data-table th {
          position: sticky;
          top: 0;
          z-index: 1;
          background: #ffffff;
          color: #475569;
          font-size: 0.74rem;
          text-transform: uppercase;
          letter-spacing: 0;
        }
        .candidates-data-table td {
          color: #0f172a;
          font-weight: 750;
        }
        .candidates-data-table tr {
          cursor: pointer;
        }
        .candidates-data-table tbody tr.is-hovered,
        .candidates-data-table tbody tr:hover {
          background: #f8fafc;
        }
        .candidates-data-table tbody tr.is-selected {
          background: #eff6ff;
          outline: 2px solid rgba(22, 102, 215, 0.16);
          outline-offset: -2px;
        }
        .candidates-difference-value {
          font-weight: 900;
        }
        .candidates-difference-value.is-positive {
          color: ${BRAND_COLORS.primaryBrightBlue};
        }
        .candidates-difference-value.is-negative {
          color: ${BRAND_COLORS.accentRed};
        }
        .candidates-difference-value.is-zero {
          color: #334155;
        }
        .candidates-tooltip {
          position: fixed;
          z-index: 1800;
          width: max-content;
          min-width: 160px;
          max-width: 270px;
          border: 1px solid rgba(15, 23, 42, 0.14);
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.98);
          box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18);
          padding: 8px 10px;
          pointer-events: none;
        }
        .candidates-tooltip-title {
          font-weight: 900;
          margin-bottom: 5px;
          color: #0f172a;
          font-size: 0.86rem;
          line-height: 1.2;
        }
        .candidates-tooltip-grid {
          display: grid;
          grid-template-columns: 74px minmax(0, 1fr);
          gap: 5px 10px;
          font-size: 0.84rem;
        }
        .candidates-tooltip-grid span {
          color: #475569;
        }
        .candidates-tooltip-grid strong {
          min-width: 0;
          overflow-wrap: anywhere;
        }
        @media (max-width: 960px) {
          .candidates-shell {
            grid-template-columns: minmax(0, 1fr);
          }
          .candidates-panel {
            max-height: none;
          }
          .candidates-map-frame {
            height: clamp(340px, calc(var(--app-shell-available-height, 100vh) - 170px), 480px);
          }
        }
      `}</style>
      <section className="candidates-shell" aria-label="Kandidáti">
        <aside className="candidates-panel">
          <label className="candidates-control">
            <span>Volby</span>
            <select value={electionId} onChange={(event) => setElectionId(event.target.value)} disabled={isLoadingElections || !areaId}>
              {elections.length === 0 ? <option value="">Bez dostupných voleb</option> : null}
              {elections.map((election) => (
                <option key={String(election.id)} value={String(election.id)}>
                  {getElectionLabel(election)}
                </option>
              ))}
            </select>
          </label>

          {canShowCandidateControls ? (
            <>
              <label className="candidates-control">
                <span>Kandidátka</span>
                <select value={selectedListId} onChange={(event) => setSelectedListId(event.target.value)} disabled={lists.length === 0}>
                  {lists.length === 0 ? <option value="">Bez kandidátek</option> : null}
                  {lists.map((list) => (
                    <option key={String(list.list_id)} value={String(list.list_id)}>
                      {list.party_name || list.list_id}
                    </option>
                  ))}
                </select>
              </label>

              <label className="candidates-control">
                <span>Kandidát</span>
                <select
                  value={selectedCandidateId}
                  onChange={(event) => setSelectedCandidateId(event.target.value)}
                  disabled={selectableCandidates.length === 0}
                >
                  {selectableCandidates.length === 0 ? <option value="">Bez kandidátů</option> : null}
                  {selectableCandidates.map((candidate) => (
                    <option key={String(candidate.candidate_id)} value={String(candidate.candidate_id)}>
                      {getCandidateDisplayName(candidate)}
                    </option>
                  ))}
                </select>
              </label>

              <div className="candidates-control">
                <span>Režim</span>
                <div className="candidates-mode-row">
                  <button
                    type="button"
                    className={`candidates-mode-button${metricMode === CANDIDATE_MODE_VALUES ? " is-active" : ""}`}
                    onClick={() => setMetricMode(CANDIDATE_MODE_VALUES)}
                  >
                    Hodnoty
                  </button>
                  <button
                    type="button"
                    className={`candidates-mode-button${metricMode === CANDIDATE_MODE_PERCENT ? " is-active" : ""}`}
                    onClick={() => setMetricMode(CANDIDATE_MODE_PERCENT)}
                  >
                    Procenta
                  </button>
                </div>
              </div>
            </>
          ) : null}

          <div className="candidates-panel-actions">
            <button type="button" className="candidates-report-button" disabled title="Report rendering pro Kandidáty zatím není součástí MVP.">
              Uložit do reportu
            </button>
          </div>

          {canShowCandidateControls && metricMode === CANDIDATE_MODE_PERCENT ? (
            <div className="candidates-legend">
              <div className="candidates-section-label">Legenda</div>
              <div className="candidates-legend-control-row">
                <span className="candidates-legend-text">Barva škály</span>
                <CandidatesColorPresetPicker
                  label="Barva škály"
                  value={baseColor}
                  openPicker={openPicker}
                  setOpenPicker={setOpenPicker}
                  onChange={setBaseColor}
                />
              </div>
              <MapLegend legendItems={legendItems} />
            </div>
          ) : null}

          {emptyState ? <div className="candidates-status"><span>{emptyState}</span></div> : null}
          {loadingText || softWarnings.length > 0 ? (
            <div className="candidates-status" role={softWarnings.length > 0 ? "alert" : "status"}>
              {loadingText ? <span>Načítám: {loadingText}</span> : null}
              {softWarnings.map((warning) => (
                <span key={warning} className="is-error">{warning}</span>
              ))}
            </div>
          ) : null}
        </aside>

        <section className="candidates-map-frame" aria-label="Mapa kandidátů">
          <MapPane
            mapKey={mapKey}
            geoJsonKey={geoJsonKey}
            districtsGeoJson={data.districtsGeoJson || EMPTY_GEOJSON}
            styleFeature={styleFeature}
            onEachFeature={onEachFeature}
            mapStandard={ELECTION_MAP_STANDARD_V1_KEY}
            height="100%"
            scrollWheelZoom={false}
            initialView={initialViewport}
            fitGeoJsonBounds
            fitBoundsRequestKey={fitRequestKey}
            fitBoundsPadding={[28, 28]}
            fitBoundsMaxZoom={14}
            onMapReady={(map) => {
              mapRef.current = map;
            }}
            onViewChange={(center, zoom) => {
              writeStoredViewport("candidates", areaId, { center, zoom });
            }}
            onMouseLeave={() => {
              setHoveredPrecinct(null);
              setTooltip(null);
            }}
            onInteractionStart={() => {
              setHoveredPrecinct(null);
              setTooltip(null);
            }}
            onResetView={resetMapToArea}
          >
            {metricMode === CANDIDATE_MODE_VALUES ? <CandidateValueLabelsLayer labels={valueLabels} isMunicipal={isMunicipal} /> : null}
          </MapPane>
          {geometryBlockingError ? <div className="candidates-map-error">{geometryBlockingError}</div> : null}
          {!geometryBlockingError && emptyState ? <div className="candidates-map-empty">{emptyState}</div> : null}
        </section>
      </section>

      <section className="candidates-data-section" aria-label="Data kandidátů">
        <div className="candidates-data-header">
          <div className="candidates-data-title">
            <h2>{dataTitle}</h2>
          </div>
          <div className="candidates-data-actions">
            <label className="candidates-data-control" htmlFor="candidates-comparison">
              <span className="candidates-section-label">Srovnat</span>
              <select
                id="candidates-comparison"
                value={comparisonCandidateId}
                onChange={(event) => setComparisonCandidateId(event.target.value)}
                disabled={comparisonCandidateOptions.length === 0}
              >
                <option value="">Bez srovnání</option>
                {comparisonCandidateOptions.map((candidate) => (
                  <option key={String(candidate.candidate_id)} value={String(candidate.candidate_id)}>
                    {getCandidateDisplayName(candidate)}
                  </option>
                ))}
              </select>
            </label>
            <label className="candidates-data-control" htmlFor="candidates-sort">
              <span className="candidates-section-label">Řazení</span>
              <select id="candidates-sort" value={sortKey} onChange={(event) => setSortKey(event.target.value)}>
                <option value="precinct">Číslo okrsku</option>
                <option value="precinct_size">Velikost okrsku</option>
                <option value="percent">Procenta</option>
                <option value="absolute">{isMunicipal ? "Bonusové hlasy" : "Hlasy kandidáta"}</option>
                {comparisonCandidate ? <option value="difference">Rozdíl</option> : null}
              </select>
              <button
                type="button"
                className="candidates-sort-direction-button"
                aria-label={sortDirection === "asc" ? "Řadit sestupně" : "Řadit vzestupně"}
                title={sortDirection === "asc" ? "Vzestupně" : "Sestupně"}
                onClick={() => setSortDirection((current) => (current === "asc" ? "desc" : "asc"))}
              >
                {sortDirection === "asc" ? "↑" : "↓"}
              </button>
            </label>
          </div>
        </div>
        {emptyState ? (
          <p className="candidates-data-empty">{emptyState}</p>
        ) : sortedRows.length === 0 ? (
          <p className="candidates-data-empty">Pro zvolenou kombinaci zatím nejsou k dispozici řádky.</p>
        ) : (
          <div className="candidates-data-table-wrap">
            <table className="candidates-data-table">
              <thead>
                <tr>
                  <th>Č. okrsku</th>
                  <th>Kandidát</th>
                  <th>Procenta</th>
                  <th>Hodnota</th>
                  {comparisonCandidate ? <th>Rozdíl</th> : null}
                  {comparisonCandidate ? <th>{comparisonCandidate.candidate_name || "Srovnávaný kandidát"}</th> : null}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => {
                  const isHovered = hoveredPrecinct != null && String(hoveredPrecinct) === String(row.precinctId);
                  const isSelected = selectedPrecinctIds.has(String(row.precinctId));
                  return (
                    <tr
                      key={row.precinctId}
                      className={`${isHovered ? "is-hovered" : ""}${isSelected ? " is-selected" : ""}`}
                      onMouseEnter={() => setHoveredPrecinct(row.precinctId)}
                      onMouseLeave={() => setHoveredPrecinct((current) => (String(current || "") === String(row.precinctId) ? null : current))}
                      onClick={() => toggleSelectedPrecinct(row.precinctId)}
                    >
                      <td>{row.precinctId}</td>
                      <td>{row.candidateName}</td>
                      <td>{row.percentValue == null ? "–" : formatPct(row.percentValue)}</td>
                      <td>{formatValueForMode(row, isMunicipal)}</td>
                      {comparisonCandidate ? (
                        <td>
                          <span className={`candidates-difference-value ${getDifferenceClassName(row.differenceValue)}`}>
                            {formatDifferenceValue(row.differenceValue)}
                          </span>
                        </td>
                      ) : null}
                      {comparisonCandidate ? <td>{formatValueForMode(row.comparisonRow, isMunicipal)}</td> : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {tooltip && tooltipRow ? (
        <div className="candidates-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
          <div className="candidates-tooltip-title">{tooltipRow.precinctLabel}</div>
          <div className="candidates-tooltip-grid">
            <span>Kandidát</span>
            <strong>{tooltipRow.candidateName}</strong>
            <span>Hodnota</span>
            <strong>{formatValueForMode(tooltipRow, isMunicipal)}</strong>
            <span>Procenta</span>
            <strong>{tooltipRow.percentValue == null ? "–" : formatPct(tooltipRow.percentValue)}</strong>
            {isMunicipal && tooltipRow.referenceCandidateName ? (
              <>
                <span>Reference</span>
                <strong>
                  {tooltipRow.referenceCandidateName} ({tooltipRow.referenceVotes == null ? "–" : formatInt(tooltipRow.referenceVotes)})
                </strong>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </main>
  );
}
